const API_URL = process.env.REACT_APP_API_URL;
const AGENTS_URL = `${API_URL}/api/agent-services`;

/**
 * Get services for an agent
 * @param {string} agentId - Agent ID
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {string} type - Filter type: 'all', 'discover', 'added', 'owned'
 */
export const getAgentServices = async (agentId, page = 1, limit = 24, type = 'all') => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services?page=${page}&limit=${limit}&type=${type}`,
        {
            credentials: 'include',
        }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch agent services');
    }

    return response.json();
};

/**
 * Add service to agent
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 */
export const addAgentService = async (agentId, serviceId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/add`,
        {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add service to agent');
    }

    return response.json();
};

/**
 * Remove service from agent
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 */
export const removeAgentService = async (agentId, serviceId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}`,
        {
            method: 'DELETE',
            credentials: 'include',
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove service from agent');
    }

    return response.json();
};

/**
 * Toggle agent service enabled state
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 */
export const toggleAgentService = async (agentId, serviceId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/toggle`,
        {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle agent service');
    }

    return response.json();
};

/**
 * Get tools for agent service
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 */
export const getAgentTools = async (agentId, serviceId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/tools`,
        {
            credentials: 'include',
        }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch agent tools');
    }

    return response.json();
};

/**
 * Toggle agent tool enabled state
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 * @param {string} toolId - Tool ID
 */
export const toggleAgentTool = async (agentId, serviceId, toolId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/tools/${toolId}/toggle`,
        {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to toggle agent tool');
    }

    return response.json();
};

// Agent Secrets API

/**
 * Get agent secrets for service
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 */
export const getAgentSecrets = async (agentId, serviceId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/secrets`,
        {
            credentials: 'include',
        }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch agent secrets');
    }

    return response.json();
};

/**
 * Create or update agent secret
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 * @param {string} key - Secret key
 * @param {string} value - Secret value
 * @param {boolean} isEncrypted - Whether to encrypt the value
 */
export const upsertAgentSecret = async (agentId, serviceId, key, value, isEncrypted = true) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/secrets/${key}`,
        {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ value, isEncrypted }),
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save agent secret');
    }

    return response.json();
};

/**
 * Delete agent secret
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 * @param {string} key - Secret key
 */
export const deleteAgentSecret = async (agentId, serviceId, key) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/secrets/${key}`,
        {
            method: 'DELETE',
            credentials: 'include',
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete agent secret');
    }

    return response.json();
};

/**
 * Copy user secrets to agent
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 */
export const copyUserSecretsToAgent = async (agentId, serviceId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/secrets/copy-user`,
        {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to copy user secrets to agent');
    }

    return response.json();
};

// Agent OAuth API

/**
 * Get agent OAuth token info
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 */
export const getAgentToken = async (agentId, serviceId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/oauth/token`,
        {
            credentials: 'include',
        }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch agent token');
    }

    return response.json();
};

/**
 * Delete agent OAuth token
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 */
export const deleteAgentToken = async (agentId, serviceId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/oauth/token`,
        {
            method: 'DELETE',
            credentials: 'include',
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete agent token');
    }

    return response.json();
};

/**
 * Get agent OAuth authorization URL
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 * @param {string} redirectUri - Redirect URI
 */
export const getAgentAuthorizationUrl = async (agentId, serviceId, redirectUri) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/oauth/authorize?redirectUri=${encodeURIComponent(redirectUri)}`,
        {
            credentials: 'include',
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get authorization URL');
    }

    return response.json();
};

/**
 * Copy user OAuth token to agent
 * @param {string} agentId - Agent ID
 * @param {string} serviceId - Service ID
 */
export const copyUserTokenToAgent = async (agentId, serviceId) => {
    const response = await fetch(
        `${AGENTS_URL}/${agentId}/services/${serviceId}/oauth/copy-user`,
        {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    );

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to copy user token to agent');
    }

    return response.json();
};
